import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/shared/context/AuthContext';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', remember: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // Client-side validation (replaces browser-native validation)
    if (!form.email || !form.email.includes('@') || !form.email.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }
    if (!form.password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-navy-900 p-12 flex-col justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">ThreatLens360</h1>
            <p className="text-teal-300 text-sm">See Your Attack Surface. Before They Do.</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          <h2 className="text-4xl font-bold text-white leading-tight">
            360° Visibility Into<br />Your Security Posture
          </h2>
          <p className="text-slate-300 text-lg leading-relaxed">
            Monitor threats, manage vulnerabilities, and protect<br />
            your organization with enterprise-grade threat<br />
            intelligence.
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
              <p className="text-white font-medium">Real-time Threat Detection</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
              <p className="text-white font-medium">AI-Powered Analysis</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
              <p className="text-white font-medium">Supply Chain Security</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 rounded-full bg-teal-400 mt-2 flex-shrink-0" />
              <p className="text-white font-medium">Compliance Reporting</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <p className="text-slate-400 text-sm">Trusted by security teams worldwide</p>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">ThreatLens360</h1>
            </div>
            <p className="text-gray-600 text-sm">See Your Attack Surface. Before They Do.</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8" data-testid="login-form">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
              <p className="text-gray-600 mt-2">Sign in to access your security dashboard</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                <input 
                  type="email" 
                  required 
                  value={form.email} 
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                  placeholder="you@company.com" 
                  data-testid="login-email" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <input 
                    type={showPassword ? "text" : "password"}
                    required 
                    value={form.password} 
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors pr-12"
                    placeholder="••••••••••" 
                    data-testid="login-password" 
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={form.remember}
                    onChange={e => setForm(f => ({ ...f, remember: e.target.checked }))}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500" 
                  />
                  <span className="text-sm text-gray-700">Remember me</span>
                </label>
                <Link to="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700 font-medium">
                  Forgot password?
                </Link>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign in to Dashboard'}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                Secure authentication
              </p>
              <p className="text-center text-xs text-gray-500 mt-4">
                Don't have an account? <Link to="/register" className="text-teal-600 font-medium hover:underline">Request access</Link>
              </p>
            </div>

            <div className="mt-6 text-center text-xs text-gray-500">
              <p>Your session will automatically expire after 15 minutes of inactivity.</p>
              <p className="mt-1">All connections are encrypted with TLS 1.3.</p>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            © 2026 ThreatLens360. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
